import { combineLatest, map } from 'rxjs';
import { BaseGraphPlugin } from './base-provider';
import type { SoundThresholdLine } from './sound-threshold-lines';
import { getSoundUnit$ } from './sound-plugin/unit-formatter';

interface RenderedSoundThresholdLine extends SoundThresholdLine {
    index: number;
    unit: string;
    x1: number;
    x2: number;
    y: number;
}

interface PositionedSoundThresholdLabel extends RenderedSoundThresholdLine {
    labelY: number;
}

const labelOffset = 4;
const minimumLabelSpacing = 14;
const labelBoundaryOffset = 12;

function groupLabelsByLevel(lines: RenderedSoundThresholdLine[]): RenderedSoundThresholdLine[] {
    const labelsByLevel = new Map<number, RenderedSoundThresholdLine[]>();

    lines.forEach((line) => {
        const labels = labelsByLevel.get(line.value) ?? [];
        labels.push(line);
        labelsByLevel.set(line.value, labels);
    });

    return Array.from(labelsByLevel.values()).map((labels) => ({
        ...labels[0],
        label: labels.map((line) => line.label).join(', '),
    }));
}

function spaceLabelsVertically(
    labels: RenderedSoundThresholdLine[],
    graphHeight: number
): PositionedSoundThresholdLabel[] {
    // SVG text is positioned on its baseline, so leave room for glyphs at both plot edges.
    const minimumLabelY = labelBoundaryOffset;
    const maximumLabelY = Math.max(minimumLabelY, graphHeight - labelBoundaryOffset);
    let previousLabelY = -Infinity;

    // Keep labels close to their line where possible, while spacing nearby labels apart.
    const positionedLabels = [...labels]
        .sort((firstLabel, secondLabel) => firstLabel.y - secondLabel.y)
        .map((label) => {
            const preferredLabelY = Math.min(
                Math.max(label.y - labelOffset, minimumLabelY),
                maximumLabelY
            );
            const labelY = Math.max(preferredLabelY, previousLabelY + minimumLabelSpacing);
            previousLabelY = labelY;

            return { ...label, labelY };
        });

    const lastLabelY = positionedLabels[positionedLabels.length - 1]?.labelY ?? minimumLabelY;
    const overflow = Math.max(0, lastLabelY - maximumLabelY);

    // Move an overflowing group up together so every label remains inside the clipped plot.
    return positionedLabels.map((label) => ({
        ...label,
        labelY: Math.max(minimumLabelY, label.labelY - overflow),
    }));
}

export class SoundThresholdLinesProvider extends BaseGraphPlugin {
    init(): void {
        const renderer = combineLatest([
            this.dataSet.soundThresholdLines$,
            // Sound samples append directly to drawArea, so redraw to keep
            // threshold overlays above them.
            this.dataSet.soundData$,
            this.graph.xAxis.scaleUpdated$,
            this.graph.yAxis.scaleUpdated$,
            getSoundUnit$(this.dataSet),
        ])
            .pipe(
                map(([lines, , , , unit]) => {
                    const renderedLines = lines.map((thresholdLine, index) => ({
                        ...thresholdLine,
                        index,
                        unit,
                        x1: this.graph.xAxis.scale(this.graph.xAxis.getMin()),
                        x2: this.graph.xAxis.scale(this.graph.xAxis.getMax()),
                        y: this.graph.yAxis.scale(thresholdLine.value),
                    }));

                    return {
                        lines: renderedLines,
                        labels: spaceLabelsVertically(
                            groupLabelsByLevel(renderedLines),
                            this.graph.graphHeight
                        ),
                    };
                })
            )
            .subscribe(({ lines, labels }) => {
                const thresholdLines = this.graph.drawArea
                    .selectAll('.soundThresholdLine')
                    .data(lines, (line) => (line as SoundThresholdLine).key)
                    .join('svg:line')
                    .attr('class', (line) => `soundThresholdLine soundThresholdLine${line.index}`)
                    .attr('x1', (line) => line.x1)
                    .attr('x2', (line) => line.x2)
                    .attr('y1', (line) => line.y)
                    .attr('y2', (line) => line.y);

                thresholdLines.raise();

                const thresholdLabels = this.graph.drawArea
                    .selectAll('.soundThresholdLineLabel')
                    .data(labels, (line) => (line as SoundThresholdLine).key)
                    .join((enter) =>
                        enter
                            .append('svg:text')
                            .attr('class', 'soundThresholdLineLabel')
                            .attr('text-anchor', 'end')
                    )
                    .attr('data-source', 'Current sound notification configuration')
                    .attr('x', (line) => line.x2 - 6)
                    .attr('y', (line) => line.labelY)
                    .text((line) => `${line.label}: ${line.value} ${line.unit}`);

                thresholdLabels.raise();
            });

        this.subscription.add(renderer);
    }
}
